import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { SquarePen, PanelLeftOpen } from "lucide-react";
import { BottomNav, /* AccountSelector, */ UserProfileBadge, ConversationDrawer } from "./components";
import { OfflineBanner } from "./components/OfflineBanner";
import { InstallPrompt } from "./components/InstallPrompt";
import { CopilotAuthProvider } from "./context/AuthContext";
import { AccountProvider } from "./context/AccountContext";
import { ChatProvider, useChatContext } from "./context/ChatContext";
import { useAuth } from "./hooks/useAuth";
import { cn } from "./lib/utils";
import "./copilot.css";

// ─── Lazy-loaded screens (code splitting) ───────────────────────────
const HomeScreen = lazy(() =>
  import("./pages/HomeScreen").then((m) => ({ default: m.HomeScreen })),
);
const RecordPage = lazy(() =>
  import("./pages/RecordPage").then((m) => ({ default: m.RecordPage })),
);
const ChatPage = lazy(() =>
  import("./pages/ChatPage").then((m) => ({ default: m.ChatPage })),
);
const AccountPage = lazy(() =>
  import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })),
);
const ReviewEditScreen = lazy(() =>
  import("./screens/ReviewEditScreen").then((m) => ({
    default: m.ReviewEditScreen,
  })),
);
const TripReportDetailScreen = lazy(() =>
  import("./pages/TripReportDetailScreen").then((m) => ({
    default: m.TripReportDetailScreen,
  })),
);
const CopilotLoginPage = lazy(() =>
  import("./pages/LoginPage").then((m) => ({
    default: m.CopilotLoginPage,
  })),
);

// ─── Loading fallback ───────────────────────────────────────────────
function ScreenLoader() {
  return (
    <div className="flex min-h-[50dvh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
    </div>
  );
}

// ─── Service worker + API prefetch ──────────────────────────────────

function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;

  // In development: actively UNREGISTER any existing service workers.
  // Just skipping registration (the old approach) left previously-registered
  // service workers active — they continued intercepting fetches and serving
  // stale cached CSS/JS, causing "colors reverting" bugs.
  if (import.meta.env.DEV) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
      // Also clear service worker caches so stale assets are gone
      if (registrations.length > 0) {
        caches.keys().then((keys) => {
          keys.filter((k) => k.startsWith("copilot-")).forEach((k) => caches.delete(k));
        });
      }
    });
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/copilot-sw.js", { scope: "/copilot" })
      .then((registration) => {
        // Listen for service worker messages
        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data?.type === "SYNC_COMPLETE") {
            // Dispatch a custom event for UI feedback
            window.dispatchEvent(
              new CustomEvent("copilot-sync-complete", {
                detail: event.data,
              }),
            );
          }
          if (event.data?.type === "GET_AUTH_TOKEN") {
            // Service worker is requesting a fresh token for background sync.
            // Respond via the MessageChannel port with the current token.
            const token = localStorage.getItem("cm_access_token");
            if (event.ports[0]) {
              event.ports[0].postMessage({ token });
            }
          }
        });

        // Prefetch critical API data once SW is ready
        prefetchCriticalData();

        return registration;
      })
      .catch(() => {
        // Service worker registration failed — non-critical
      });
  });
}

/**
 * Prefetch critical API data via service worker.
 * This populates the API cache for instant access to accounts.
 */
function prefetchCriticalData() {
  const token = localStorage.getItem("cm_access_token");
  if (!token || !navigator.serviceWorker.controller) return;

  navigator.serviceWorker.controller.postMessage({
    type: "CACHE_API_PREFETCH",
    urls: ["/api/copilot/accounts"],
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ─── Route guard ────────────────────────────────────────────────────

function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/copilot/login" replace />;
  }

  return <Outlet />;
}

// ─── Role-aware default redirect ────────────────────────────────────

function DefaultRedirect() {
  return <Navigate to="home" replace />;
}

// ─── Shell layout ───────────────────────────────────────────────────

function CopilotShell() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const chat = useChatContext();
  const navigate = useNavigate();

  const handleOpenDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const handleNewChat = useCallback(() => {
    chat.clearConversation();
    navigate("/copilot/chat");
  }, [chat, navigate]);

  const handleSelectConversation = useCallback(
    (convId: string) => {
      chat.loadConversation(convId);
      navigate("/copilot/chat");
    },
    [chat, navigate],
  );

  return (
    <div className="copilot-root copilot-shell flex min-h-dvh flex-col bg-surface">
      {/* Offline indicator */}
      <OfflineBanner />

      {/* Conversation history drawer */}
      <ConversationDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        activeConversationId={chat.conversationId}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
      />

      {/* Top header bar */}
      <header className="copilot-header fixed inset-x-0 top-0 z-40 border-b border-border bg-surface/95 backdrop-blur-sm">
        <div className="flex h-header items-center justify-between px-4">
          <div className="flex min-w-0 items-center gap-2">
            <UserProfileBadge />
            <h1 className="truncate text-base font-bold text-text-primary">
              Sales Copilot
            </h1>
          </div>
          <div className="flex items-center gap-1">
            {/* New Chat button */}
            <button
              type="button"
              onClick={handleNewChat}
              aria-label="New chat"
              className={cn(
                "flex h-touch w-touch items-center justify-center rounded-full",
                "text-text-secondary active:bg-brand/10 active:text-brand",
                "transition-colors",
              )}
            >
              <SquarePen className="h-5 w-5" />
            </button>
            {/* History drawer toggle */}
            <button
              type="button"
              onClick={handleOpenDrawer}
              aria-label="Conversation history"
              className={cn(
                "flex h-touch w-touch items-center justify-center rounded-full",
                "text-text-secondary active:bg-brand/10 active:text-brand",
                "transition-colors",
              )}
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Page content (lazy-loaded screens) */}
      <main className="flex flex-1 flex-col">
        <Suspense fallback={<ScreenLoader />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Bottom navigation */}
      <BottomNav />

      {/* Install prompt (iOS + Android) */}
      <InstallPrompt />
    </div>
  );
}

// ─── Top-level Copilot app ──────────────────────────────────────────

export function CopilotApp() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <CopilotAuthProvider>
      <Suspense fallback={<ScreenLoader />}>
        <Routes>
          {/* Public: login */}
          <Route path="login" element={<CopilotLoginPage />} />

          {/* Authenticated routes — wrapped in AccountProvider for shared account state */}
          <Route element={<RequireAuth />}>
            <Route element={<AccountProvider><ChatProvider><CopilotShell /></ChatProvider></AccountProvider>}>
              <Route path="home" element={<HomeScreen />} />
              <Route path="record" element={<RecordPage />} />
              <Route path="review" element={<ReviewEditScreen />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route
                path="account/:accountId"
                element={<AccountPage />}
              />
              <Route
                path="report/:reportId"
                element={<TripReportDetailScreen />}
              />
            </Route>
          </Route>

          {/* Default redirect — role-aware landing */}
          <Route index element={<DefaultRedirect />} />

          {/* Catch-all */}
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </Suspense>
    </CopilotAuthProvider>
  );
}
